import { NavLink } from "react-router";

import { cx } from "@/lib/cx";
import type { Destination } from "@/lib/destinations";

/**
 * DESIGN.md § Components `nav-item`: 44px, `rounded.md`, `{typography.body}`
 * in `{colors.ink-secondary}`; active is `{colors.accent}` text on
 * `{colors.accent-quiet}`; stays at `body` size, not `meta`, on every
 * layout: a nav label is a value the user reads to navigate, not a caption
 * next to one, so DESIGN.md's 16px-minimum rule applies to it directly.
 * The label is always rendered, real text, on every layout — icon-only
 * navigation is banned (EXPERIENCE.md § Information Architecture).
 *
 * The bottom bar and the floating bar (`"floating"`) both use
 * `destination.shortLabel` when one exists (owner decision, see
 * `destinations.ts`) — `"sheet"` (the "More" panel, with a whole column to
 * itself) is the one layout that still uses the full `label`.
 * Found live, Story 17.1's own Tier B review (2026-09): the floating bar
 * has five real nav slots sharing one row alongside a wordmark, a page
 * title and a utility cluster — "Tenants & Leases"' own full label alone
 * pushed the bar's min-content width past what a real ~1024px-wide desktop
 * window has room for, clipping the alert bell and the account/sign-out
 * menu off-screen entirely. `shortLabel` is real, readable text ("Leases"),
 * never an icon standing alone — EXPERIENCE.md's "icon-only navigation is
 * banned" rule is unaffected.
 *
 * Active state comes from `NavLink`'s own URL match, not local component
 * state — the same mechanism that makes a pasted URL or a hard reload
 * restore the right destination (VC-2.1-10) restores which item is marked
 * active, by construction.
 *
 * `layout` is a small lookup, not a second copy of this component — every
 * prop, every size class and the whole DOM shape stay identical across all
 * three values; only the wrapping element's own flex direction/padding and
 * (for `bottom-bar`) the label's text alignment differ.
 *
 * Story 17.1 — `"rail"` (the collapsed-icon-strip desktop sidebar's own
 * dark-on-hover-expand treatment, Story 15.18) is gone along with
 * `NavRail.tsx` itself: the floating nav bar that replaced it uses
 * `"floating"` instead — a plain always-labeled pill item, exactly like
 * `"sheet"`'s resting/active colors, just laid out horizontally with the
 * pill bar's own tighter padding. There is no hover-reveal/collapse
 * behavior left anywhere in this component.
 */
export function NavItem({
  destination,
  layout,
  onNavigate,
}: {
  destination: Destination;
  layout: "bottom-bar" | "sheet" | "floating";
  onNavigate?: () => void;
}) {
  const Icon = destination.icon;
  const label =
    layout === "bottom-bar" || layout === "floating"
      ? (destination.shortLabel ?? destination.label)
      : destination.label;

  return (
    <NavLink
      to={destination.path}
      end={destination.path === "/"}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          "rounded-md text-body outline-offset-2",
          isActive ? "bg-accent-quiet text-accent" : "text-ink-secondary hover:bg-surface-hover",
          layout === "sheet" && "flex h-tap-target-min items-center gap-3 px-4",
          layout === "floating" && "flex h-tap-target-min items-center gap-2 px-3",
          layout === "bottom-bar" &&
            "flex h-full w-full min-w-tap-target-min flex-col items-center justify-center gap-0.5 px-0.5",
        )
      }
    >
      <Icon className="shrink-0" />
      <span className={layout === "bottom-bar" ? "text-center" : undefined}>{label}</span>
    </NavLink>
  );
}
